import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import { ModalTypes } from '../AtBat/atbat.style';
import ModalWithAnimation from '@mlb/react-patterns/v4/components/ModalWithAnimation/modalWithAnimation';
import {
    BackdropWrapper,
    ContentWrapper,
    CloseButton,
    CustomModalContent,
    CustomVerticalModalContent,
    PortalLessBackdropWrapper
} from './overlay.style';

import COLORS from '@mlb/react-patterns/v4/style/universal.color';

const mobileProps = {
    ContentComponent: CustomVerticalModalContent,
    BackdropComponent: BackdropWrapper,
    backdropProps: { invisible: false },
    contentProps: {
        variants: {
            hide: {
                y: '100%',
                transition: { stiffness: 100 }
            },
            show: {
                y: 0,
                transition: { delay: 0.2, stiffness: 100 }
            }
        },
        style: {
            display: 'flex',
            justifyContent: 'center'
        }
    },
    disableBackdropClick: false,
    disableScrollLock: false,
    position: { bottom: '0', left: '0' },
    width: '100%'
};

const { mlbBlack } = COLORS;

const Overlay = forwardRef(({
    isOpen,
    children,
    type,
    onClose,
    ContentComponent,
    disableCloseButton = false,
    backdropEventType = 'onClick',
    ...otherProps
}, ref) => {
    let modalProps = {};
    const ariaLabel = useTranslator('app_close', 'Close');
    const baseProps = {
        ContentComponent: CustomModalContent,
        isCentered: true,
        isOpen,
        hideCloseButton: true,
        onClose,
        backdropEventType
    };

    modalProps = { ...baseProps };
    const {
        VERTICAL,
        FIELDVISION_VERTICAL,
        ATBAT_VERTICAL,
        PORTAL_LESS,
        SCOREBOARD_VERTICAL
    } = ModalTypes;

    if (type === VERTICAL || type === FIELDVISION_VERTICAL || type === ATBAT_VERTICAL || type === SCOREBOARD_VERTICAL) {
        modalProps = { ...baseProps, ...mobileProps };
    }

    if (type === PORTAL_LESS) {
        modalProps = { ...baseProps, ...{ BackdropComponent: PortalLessBackdropWrapper } };
    }

    if (ContentComponent) {
        modalProps = { ...modalProps, ...{ ContentComponent } };
    }

    return (
        <ModalWithAnimation {...modalProps} {...otherProps}>
            <ContentWrapper type={type} ref={ref}>
                { !disableCloseButton
                    ? (
                        <CloseButton
                            iconName='close-thin'
                            iconSize='20'
                            iconColor={'var(--gameday-primary-font-color)'}
                            iconHoverColor={'var(--gameday-primary-font-color)'}
                            handlers={{ onClick: onClose }}
                            type={type}
                            ariaLabel={ariaLabel}
                        />
                    )
                    : null}
                {children}
            </ContentWrapper>
        </ModalWithAnimation>
    );
});

Overlay.displayName = 'Overlay';

Overlay.propTypes = {
    onClose: PropTypes.func,
    isOpen: PropTypes.bool,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node
    ]),
    type: PropTypes.string,
    disableCloseButton: PropTypes.bool
};

export default Overlay;
